import { useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import CreateAssetModalContext from '@atom/components/common/createAssetModal/CreateAssetModalContext';
import { ASSET_ROUTE_AUTOCOMPLETE } from '@atom/graph/asset';
import { getGoogleShapeCenter, useReverseGeocoder } from '@atom/map';
import { NumericFormat } from '@atom/mui';
import { GeoJSONGeometry } from '@atom/types/geojson';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import {
  buildSimpleInput,
  computeAutoFillUpdates,
  isNumberComponent,
  mapAutocompleteToOptions,
} from './assetRouteUtilities';
import MilePostInput from './MilePostInput';
import RouteSelectInput from './RouteSelectInput';

import modalClasses from '../createAssetModal.module.css';
import classes from './assetRouteTab.module.css';

const AssetRouteTab = () => {
  const {
    location,
    setLocation,
    setAddress,
    routeData,
    setRouteData,
    routeOptions,
    setRouteOptions,
    milePost,
    setMilePost,
    milePostRanges,
    setMilePostRanges,
    isRouteSnapped,
    setIsRouteSnapped,
    currentRouteAutoCompleteDone,
    setCurrentRouteAutoCompleteDone,
  } = useContext(CreateAssetModalContext);

  const [runAutocomplete, { loading }] = useMutation(ASSET_ROUTE_AUTOCOMPLETE);
  const reverseGeocode = useReverseGeocoder();

  useEffect(() => {
    // A snapped response is already resolved, so the state it sets must not
    // trigger another autocomplete request
    if (currentRouteAutoCompleteDone) {
      setCurrentRouteAutoCompleteDone(false);
      return;
    }

    const getAutocomplete = async () => {
      const input = buildSimpleInput(location, routeData);
      const res = await runAutocomplete({ variables: { input } });
      const data = res?.data?.assetRouteAutocomplete;

      setRouteOptions(mapAutocompleteToOptions(data));
      setMilePostRanges(data?.milePostRanges || []);

      const updates = computeAutoFillUpdates(routeData, data?.components);
      const snappedGeometry = data?.snappedGeometry;
      const isSnapped = !R.isNil(snappedGeometry);
      const isLocationMovedBySnapping =
        isSnapped && !R.equals(snappedGeometry, location);

      // Only when snapped, and only if something changed: An unsnapped
      // response is incomplete, so the rerun it triggers must still run. And
      // a skip is only flagged when this response changes a dependency of the
      // effect; otherwise no rerun happens to consume it
      setCurrentRouteAutoCompleteDone(
        isLocationMovedBySnapping || (isSnapped && !isNilOrEmpty(updates)),
      );

      if (!isNilOrEmpty(updates)) {
        setRouteData({ ...routeData, ...updates });
      }

      setIsRouteSnapped(isSnapped);

      if (isLocationMovedBySnapping) {
        setLocation(snappedGeometry);
        setAddress(
          await reverseGeocode(
            getGoogleShapeCenter(snappedGeometry as GeoJSONGeometry),
          ),
        );
      }
    };

    if (location) {
      getAutocomplete();
    }
  }, [location, routeData]);

  const updateRouteValue = (title: string, value: string | null) => {
    if (isNilOrEmpty(value)) {
      setRouteData(R.map(() => null, routeData));
      setMilePost(null);
      return;
    }

    setRouteData({ ...routeData, [title]: value });
    setMilePost(null);
  };

  const titles = R.keys(routeOptions);
  const milePostDisabled = loading || !milePostRanges.length;

  return (
    <div className={modalClasses.tabContainer}>
      <div className={modalClasses.tabTitle}>Location Details</div>
      <div className={classes.inputSection}>
        {titles.map((title: string) =>
          isNumberComponent(routeOptions[title]) ? (
            // Read-only: the server computes number components from the location
            <NumericFormat
              key={title}
              label={`${title} *`}
              value={routeData[title] ?? ''}
              disabled
            />
          ) : (
            <RouteSelectInput
              key={title}
              title={title}
              // Enumerated components only ever hold a string
              value={routeData[title] as string}
              options={routeOptions[title]?.enumeration || []}
              onChange={updateRouteValue}
              loading={loading}
            />
          ),
        )}
        {!isRouteSnapped && (
          <MilePostInput
            value={milePost}
            ranges={milePostRanges}
            onChange={setMilePost}
            disabled={milePostDisabled}
          />
        )}
      </div>
    </div>
  );
};

export default AssetRouteTab;
